import { ConversationListItem } from "@/services/conversations.api";

/** Lo que `inbox:update` manda desde el backend (ver `inbox-update.ts` del conversation-service). */
export interface InboxUpdate {
  conversationId: string;
  channelId: string;
  lastMessageText?: string | null;
  lastMessageAt?: string | null;
  lastMessageSender?: string | null;
  unreadCount?: number;
  status?: string;
}

/**
 * Aplica un `inbox:update` sobre la lista **sin re-pedirla**, o dice que hay que re-pedirla.
 *
 * El problema que resuelve: cada mensaje entrante disparaba un refetch de 100 filas, la lista se
 * reconstruía entera y el scroll saltaba al tope. Buscar algo en la bandeja mientras entran
 * mensajes era imposible.
 *
 * Reordenar del lado del cliente es seguro porque la regla del servidor es una sola y simple:
 * `ORDER BY last_message_at DESC NULLS LAST`. Se replica igual, así que las dos listas no pueden
 * divergir.
 *
 * Devuelve `null` cuando el parche NO alcanza y hay que caer al refetch. Son dos casos:
 *
 * 1. **La conversación no está en la lista.** Es nueva, o cae fuera del filtro o la búsqueda
 *    activa. Una fila necesita nombre del contacto, canal, store, dueño — datos que este evento no
 *    trae y que no se pueden inventar.
 * 2. **Cambió el `status`.** Un mensaje reabre una conversación cerrada. Si el operador está
 *    filtrando por cerradas, esa fila tiene que desaparecer, y parchearla la dejaría ahí.
 */
export function aplicarInboxUpdate(
  lista: ConversationListItem[],
  u: InboxUpdate,
): ConversationListItem[] | null {
  const i = lista.findIndex((c) => c.conversation_id === u.conversationId);
  if (i === -1) return null;

  const actual = lista[i];
  if (u.status && u.status !== actual.status) return null;

  const parchada: ConversationListItem = {
    ...actual,
    // `??` y no `||`: un preview vacío o un contador en 0 son valores, no ausencias.
    last_message_text: u.lastMessageText ?? actual.last_message_text,
    last_message_at: u.lastMessageAt ?? actual.last_message_at,
    last_message_sender:
      (u.lastMessageSender as ConversationListItem["last_message_sender"]) ??
      actual.last_message_sender,
    unread_count: u.unreadCount ?? actual.unread_count,
  };

  const resto = [...lista.slice(0, i), ...lista.slice(i + 1)];
  return ordenarPorUltimoMensaje([parchada, ...resto]);
}

/**
 * El orden del servidor: `last_message_at DESC NULLS LAST`.
 *
 * No alcanza con mover la fila al tope. Un mensaje del backlog llega con una fecha VIEJA —el
 * `isNewer` del backend—, así que sube el contador de no leídos sin mover la conversación de lugar.
 * Meterla al tope en ese caso pondría un chat de hace dos días arriba de los de ahora.
 */
export function ordenarPorUltimoMensaje(
  lista: ConversationListItem[],
): ConversationListItem[] {
  return [...lista].sort((a, b) => {
    if (!a.last_message_at && !b.last_message_at) return 0;
    if (!a.last_message_at) return 1;
    if (!b.last_message_at) return -1;
    return b.last_message_at.localeCompare(a.last_message_at);
  });
}
